import React, { useState } from 'react';
import { FiSettings, FiUser, FiLock, FiSliders, FiInfo, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import AdminLayout from '../components/layout/AdminLayout';

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [notificationSettings, setNotificationSettings] = useState({
    emailAlerts: true,
    browserNotifications: true,
    userRegistrations: false,
    transactionAlerts: true,
    maintenanceAlerts: true
  });
  
  const [profileForm, setProfileForm] = useState({
    name: 'Admin User',
    email: 'admin@example.com',
    phone: '+1 (555) 123-4567'
  });
  
  const [isProfileSaved, setIsProfileSaved] = useState(false);
  
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm({
      ...profileForm,
      [name]: value
    });
    setIsProfileSaved(false);
  };
  
  const saveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulating API call
    setTimeout(() => {
      setIsProfileSaved(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setIsProfileSaved(false);
      }, 3000);
    }, 500);
  };
  
  const toggleNotificationSetting = (setting: keyof typeof notificationSettings) => {
    setNotificationSettings({
      ...notificationSettings,
      [setting]: !notificationSettings[setting]
    });
  };
  
  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Settings navigation */}
          <div className="lg:col-span-1">
            <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="font-semibold">Settings</h2>
              </div>
              
              <div className="divide-y divide-border">
                <button 
                  className={`w-full px-4 py-3 text-left flex items-center hover:bg-card/30 ${activeTab === 'profile' ? 'bg-card/50' : ''}`}
                  onClick={() => setActiveTab('profile')}
                >
                  <FiUser className="mr-3" size={18} />
                  <span>Profile</span>
                </button>
                
                <button 
                  className={`w-full px-4 py-3 text-left flex items-center hover:bg-card/30 ${activeTab === 'security' ? 'bg-card/50' : ''}`}
                  onClick={() => setActiveTab('security')}
                >
                  <FiLock className="mr-3" size={18} />
                  <span>Security</span>
                </button>
                
                <button 
                  className={`w-full px-4 py-3 text-left flex items-center hover:bg-card/30 ${activeTab === 'notifications' ? 'bg-card/50' : ''}`}
                  onClick={() => setActiveTab('notifications')}
                >
                  <FiSettings className="mr-3" size={18} />
                  <span>Notifications</span>
                </button>
                
                <button 
                  className={`w-full px-4 py-3 text-left flex items-center hover:bg-card/30 ${activeTab === 'system' ? 'bg-card/50' : ''}`}
                  onClick={() => setActiveTab('system')}
                >
                  <FiSliders className="mr-3" size={18} />
                  <span>System</span>
                </button>
                
                <button 
                  className={`w-full px-4 py-3 text-left flex items-center hover:bg-card/30 ${activeTab === 'about' ? 'bg-card/50' : ''}`}
                  onClick={() => setActiveTab('about')}
                >
                  <FiInfo className="mr-3" size={18} />
                  <span>About</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Settings content */}
          <div className="lg:col-span-3">
            <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-border">
                <h2 className="text-lg font-semibold">
                  {activeTab === 'profile' && 'Profile Settings'}
                  {activeTab === 'security' && 'Security Settings'}
                  {activeTab === 'notifications' && 'Notification Preferences'}
                  {activeTab === 'system' && 'System Settings'}
                  {activeTab === 'about' && 'About This System'}
                </h2>
              </div>
              
              <div className="p-6">
                {/* Profile Settings */}
                {activeTab === 'profile' && (
                  <form onSubmit={saveProfile}>
                    {isProfileSaved && (
                      <div className="mb-6 p-3 bg-green-100 text-green-800 rounded-lg flex items-center">
                        <FiCheckCircle className="mr-2" size={18} />
                        Profile updated successfully!
                      </div>
                    )}
                    
                    <div className="space-y-6">
                      <div className="flex flex-col">
                        <div className="flex flex-col mb-4">
                          <label className="mb-2 text-sm font-medium text-text/70">
                            Profile Picture
                          </label>
                          <div className="flex items-center">
                            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium text-2xl mr-4">
                              {profileForm.name.charAt(0)}
                            </div>
                            <button type="button" className="bg-card/50 hover:bg-card-hover text-text/70 rounded-lg py-2 px-4 transition-colors">
                              Change Avatar
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-text/70 mb-1">
                              Full Name
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={profileForm.name}
                              onChange={handleProfileChange}
                              className="w-full bg-card/30 border border-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-text/70 mb-1">
                              Email Address
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={profileForm.email}
                              onChange={handleProfileChange}
                              className="w-full bg-card/30 border border-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <label htmlFor="phone" className="block text-sm font-medium text-text/70 mb-1">
                            Phone
                          </label>
                          <input
                            type="text"
                            id="phone"
                            name="phone"
                            value={profileForm.phone}
                            onChange={handleProfileChange}
                            className="w-full bg-card/30 border border-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <button 
                        type="submit"
                        className="bg-primary text-white rounded-lg py-2.5 px-5 hover:bg-primary/90 transition-colors"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                )}
                
                {/* Notification Settings */}
                {activeTab === 'notifications' && (
                  <div>
                    <p className="text-text/70 mb-4">
                      Control which notifications you receive from the system.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-card/30 rounded-lg">
                        <div>
                          <h3 className="font-medium">Email Alerts</h3>
                          <p className="text-sm text-text/50">Receive important updates via email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="sr-only peer" 
                            checked={notificationSettings.emailAlerts}
                            onChange={() => toggleNotificationSetting('emailAlerts')}
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-card/30 rounded-lg">
                        <div>
                          <h3 className="font-medium">Browser Notifications</h3>
                          <p className="text-sm text-text/50">Get real-time notifications in browser</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="sr-only peer" 
                            checked={notificationSettings.browserNotifications}
                            onChange={() => toggleNotificationSetting('browserNotifications')}
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-card/30 rounded-lg">
                        <div>
                          <h3 className="font-medium">User Registration Alerts</h3>
                          <p className="text-sm text-text/50">Be notified when new users register</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="sr-only peer" 
                            checked={notificationSettings.userRegistrations}
                            onChange={() => toggleNotificationSetting('userRegistrations')}
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-card/30 rounded-lg">
                        <div>
                          <h3 className="font-medium">Transaction Alerts</h3>
                          <p className="text-sm text-text/50">Get notified about significant transactions</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="sr-only peer" 
                            checked={notificationSettings.transactionAlerts}
                            onChange={() => toggleNotificationSetting('transactionAlerts')}
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <button 
                        type="button"
                        className="bg-primary text-white rounded-lg py-2.5 px-5 hover:bg-primary/90 transition-colors"
                      >
                        Save Preferences
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Security Settings */}
                {activeTab === 'security' && (
                  <div>
                    <div className="p-4 mb-6 bg-yellow-100 text-yellow-800 rounded-lg flex items-center">
                      <FiAlertTriangle className="mr-2" size={18} />
                      It's recommended to change your password regularly for better security.
                    </div>
                    
                    <form>
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="currentPassword" className="block text-sm font-medium text-text/70 mb-1">
                            Current Password
                          </label>
                          <input
                            type="password"
                            id="currentPassword"
                            name="currentPassword"
                            className="w-full bg-card/30 border border-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="newPassword" className="block text-sm font-medium text-text/70 mb-1">
                            New Password
                          </label>
                          <input
                            type="password"
                            id="newPassword"
                            name="newPassword"
                            className="w-full bg-card/30 border border-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-text/70 mb-1">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            className="w-full bg-card/30 border border-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                        
                        <div className="mt-4">
                          <h3 className="font-medium mb-2">Two-Factor Authentication</h3>
                          <label className="relative inline-flex items-center cursor-pointer mb-2">
                            <input type="checkbox" className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                            <span className="ml-2 text-sm font-medium">Enable Two-Factor Authentication</span>
                          </label>
                          <p className="text-sm text-text/50">
                            Add an extra layer of security to your account by requiring both a password and a verification code.
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-6 flex justify-end">
                        <button 
                          type="submit"
                          className="bg-primary text-white rounded-lg py-2.5 px-5 hover:bg-primary/90 transition-colors"
                        >
                          Update Password
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                
                {/* System Settings placeholder */}
                {activeTab === 'system' && (
                  <div>
                    <p className="text-text/70 mb-4">
                      Configure system-wide settings and preferences.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-card/30 rounded-lg">
                        <h3 className="font-medium mb-2">Appearance</h3>
                        <div className="flex items-center space-x-4">
                          <button className="p-3 bg-white border border-border rounded-md hover:border-primary">
                            Light
                          </button>
                          <button className="p-3 bg-gray-800 text-white border border-primary rounded-md">
                            Dark
                          </button>
                          <button className="p-3 bg-gradient-to-r from-white to-gray-800 border border-border rounded-md hover:border-primary">
                            Auto
                          </button>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-card/30 rounded-lg">
                        <h3 className="font-medium mb-2">System Language</h3>
                        <select className="w-full bg-card border border-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50">
                          <option value="en">English</option>
                          <option value="es">Spanish</option>
                          <option value="fr">French</option>
                          <option value="de">German</option>
                          <option value="zh">Chinese</option>
                        </select>
                      </div>
                      
                      <div className="p-4 bg-card/30 rounded-lg">
                        <h3 className="font-medium mb-2">Data Export</h3>
                        <p className="text-sm text-text/70 mb-2">
                          Export all system data for backup or migration purposes.
                        </p>
                        <button className="bg-card hover:bg-card-hover text-text/70 rounded-lg py-2 px-4 transition-colors">
                          Export Data
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* About section placeholder */}
                {activeTab === 'about' && (
                  <div>
                    <div className="text-center mb-8">
                      <h3 className="text-xl font-bold mb-1">Matrix Finance Admin</h3>
                      <p className="text-text/70">Version 1.0.0</p>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-card/30 rounded-lg">
                        <h3 className="font-medium mb-2">System Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-text/70">Node.js Version:</span>
                            <span>16.13.1</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text/70">React Version:</span>
                            <span>18.2.0</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text/70">Database:</span>
                            <span>MongoDB 5.0.5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-text/70">Last Updated:</span>
                            <span>Apr 7, 2024</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-card/30 rounded-lg">
                        <h3 className="font-medium mb-2">License</h3>
                        <p className="text-sm text-text/70">
                          © 2024 Matrix Finance. All rights reserved. The software is provided "as is", without warranty of any kind.
                        </p>
                      </div>
                      
                      <div className="p-4 bg-card/30 rounded-lg">
                        <h3 className="font-medium mb-2">Support</h3>
                        <p className="text-sm text-text/70 mb-2">
                          Need help with the admin panel? Contact support:
                        </p>
                        <div className="text-sm">
                          <div className="mb-1">Email: support@matrixfinance.com</div>
                          <div>Phone: +1 (555) 123-4567</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings; 